/* The one definition of what a form control looks like in this system.
 *
 * Input, Select, DatePicker and Textarea are four different components — one is
 * an `<input>`, one is a button that opens a listbox, one is a button that opens
 * a calendar — but they sit next to each other in the same sheet and must read
 * as one control set. So the box itself is defined ONCE here rather than being
 * re-typed per component, which is how the four drifted apart in the first
 * place.
 *
 * Height is the part that matters most. A select that is one pixel taller than
 * the input beside it is not noticed as a bug; it is noticed as cheapness.
 */

/* Base UI (`@base-ui/react`): not a component — class-name constants and the ChoiceOption type.
   Nothing here renders, so there is no primitive to build it on. */

import { cn } from '@/lib/cn'

export type ControlSize = 'sm' | 'md'

/** 30px / 36px. `sm` is for chrome — the topbar search, a filter — never for a
 *  field inside a form sheet, where the 44px touch floor applies on phone. */
export const CONTROL_HEIGHT: Record<ControlSize, string> = {
  sm: 'h-[30px]',
  md: 'h-9',
}

/** The 15px icon that sits inside a control, and the padding that clears it. */
export const CONTROL_ICON_SIZE = 15

/* Focus is announced by the brand BORDER and the lift to white — no halo and
 * no shadow. `shadow-none` on focus is deliberate: it also clears the resting
 * inset tint, so a focused control is a flat white box with a brand border.
 *
 * Focus is applied on `focus-within` for composite controls and on `focus`
 * for a bare input, which is why the fragments live in the two class builders
 * below rather than in CONTROL_BASE. */
/* Resting controls are FILLED — a quiet sunken tint with a subtle border and a
 * hairline of inset depth — and lift to plain white when hovered or entered.
 * The fill is what makes a control read as "somewhere to type" before it is
 * touched; the lift to white is what makes the active one read as awake. */
export const CONTROL_BASE =
  'bg-surface-sunken border-border-subtle text-text-body w-full rounded-md border ' +
  'font-sans text-[13px] font-normal ' +
  'shadow-[inset_0_1px_2px_rgba(31,31,51,0.04)] ' +
  'transition-[border-color,box-shadow,background-color] ' +
  'duration-[var(--dur-fast)] ease-[var(--ease-standard)]'

export const CONTROL_INTERACTIVE =
  'hover:border-border-default hover:bg-surface ' +
  'disabled:bg-surface-disabled disabled:text-text-disabled ' +
  'disabled:cursor-not-allowed disabled:hover:border-border-subtle ' +
  'disabled:hover:bg-surface-disabled'

/** Invalid is announced by `aria-invalid` and named by the error text beside the
 *  field; the border is the third channel, never the only one. */
export const CONTROL_INVALID =
  'aria-invalid:border-negative aria-invalid:hover:border-negative'

/** A control whose focus lands on the element itself — `<input>`, `<textarea>`. */
export function controlClass(size: ControlSize = 'md', className?: string) {
  return cn(
    CONTROL_BASE,
    CONTROL_INTERACTIVE,
    CONTROL_INVALID,
    'focus:border-brand focus:bg-surface focus:outline-none focus:shadow-none',
    CONTROL_HEIGHT[size],
    className,
  )
}

/** A control that WRAPS a focusable child — the trigger button of a select or a
 *  date picker, an input with an adornment. The ring is drawn on the wrapper so
 *  it surrounds the whole box rather than an inner element. */
export function controlShellClass(size: ControlSize = 'md', className?: string) {
  return cn(
    CONTROL_BASE,
    CONTROL_INTERACTIVE,
    CONTROL_INVALID,
    'focus-within:border-brand focus-within:bg-surface focus-within:shadow-none',
    CONTROL_HEIGHT[size],
    className,
  )
}

/* ── Popover ───────────────────────────────────────────────────────────────
 * The listbox of a select and the calendar of a date picker are the same kind
 * of thing: a transient overlay anchored to its trigger. The design permits
 * exactly two things to lift off the page and this is one of them, so it
 * carries --shadow-overlay and nothing else in the system does. */
export const POPOVER_SURFACE =
  'bg-surface border-border-subtle absolute z-40 rounded-md border ' +
  'shadow-[var(--shadow-overlay)]'

/** An option row inside a listbox. Matches LanguageSelect, which is the house
 *  precedent for this pattern and must not diverge from it.
 *
 *  text-[13px], the same size as the control the list opens from (CONTROL_BASE
 *  above). It was 12.5px, which is the size this system reserves for SECONDARY
 *  text — hints, captions — and a list of primary choices set smaller than the
 *  field filtering it reads as fine print. The 249-row country list is where
 *  that became obvious enough to fix. */
export const POPOVER_OPTION =
  'flex w-full cursor-pointer items-center gap-2 rounded-[5px] px-2 py-1.5 ' +
  'text-left text-[13px] transition-colors ' +
  'duration-[var(--dur-fast)] ease-[var(--ease-standard)] ' +
  'hover:bg-surface-hover focus-visible:bg-surface-hover focus:outline-none'

/** One choice in a Select or a RadioGroup. Declared here rather than in
 *  Select.tsx so both controls and the `toOptions` helper can share it without
 *  a component file having to export a type — see below. */
export interface ChoiceOption<T extends string = string> {
  value: T
  /** A second line — a code, a country, a role. Never the only carrier of meaning. */
  label: string
  hint?: string
  disabled?: boolean
}

/** Builds options from a list of values plus a labeller — the shape almost
 *  every call site needs, where values are a union and labels come from the
 *  catalogue.
 *
 *  It lives in this module rather than beside Select because a file that
 *  exports both a component and a plain function breaks Vite's fast refresh
 *  for everything in it (react-refresh/only-export-components).
 *
 *  `const T` is load-bearing: without it the callback parameter widens to
 *  `string`, so `t(`modes.${mode}`)` becomes `modes.${string}` and i18next's
 *  typed catalogue rejects it. The key union is the whole point of that typing,
 *  and a helper that erased it would push every call site back to casting. */
export function toOptions<const T extends string>(
  values: readonly T[],
  label: (value: T) => string,
): ChoiceOption<T>[] {
  return values.map((value) => ({ value, label: label(value) }))
}
